import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import {
    createCustomLabel,
    getProformaInvoices,
    createProformaInvoice,
    deleteProformaInvoice,
} from '../../services/labelInvoice';
import { searchProductWithAkaName } from '../../services/search';
import { getOrderDetails } from '../../services/orders';
import { getB2BSalespersonList } from '../../services/b2bLeads';
import { BASE_URL } from '../../constants/apis';
import { sanitizeMobile } from '../../utils/phone';
import InPackingOrders from './InPackingOrders';
import B2BPackagingLabels from './B2BPackagingLabels';
import ConvertProformaToOrderButton from '../../components/ConvertProformaToOrderButton';
import ProformaInvoicePreview from '../../components/proforma/ProformaInvoicePreview';
import { isB2bSalesperson, getUserRole, normalizeUserRole } from '../../helpers/local_storage_helpers';

const isAdminRole = () => normalizeUserRole(getUserRole()) === 'admin';

// ═══════════════════════════════════════
// Product Search Autocomplete Component
// ═══════════════════════════════════════
export function ProductSearchInput({ value, onSelect, onChange, placeholder }) {
    const [query, setQuery] = useState(value || '');
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => { setQuery(value || ''); }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const handleSearch = useCallback(async (text) => {
        if (!text || text.length < 2) { setOptions([]); setIsOpen(false); return; }
        setIsLoading(true);
        try {
            const response = await searchProductWithAkaName(text);
            const raw = response.data?.results ?? response.data ?? [];
            const results = Array.isArray(raw) ? raw : Object.values(raw);
            const mapped = results
                .filter(Boolean)
                .map((p) => ({
                    id: p.id ?? Math.random(),
                    label: p.aka_name || p.product_name || p.name || '',
                    price: p.price ?? p.rate ?? 0,
                }))
                .filter((p) => p.label);
            setOptions(mapped);
            setIsOpen(mapped.length > 0);
        } catch (err) {
            console.error('Product search error:', err);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleInputChange = (e) => {
        const text = e.target.value;
        setQuery(text);
        onChange?.(text);
        if (!text) { setOptions([]); setIsOpen(false); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => handleSearch(text), 350);
    };

    const handleSelect = (opt) => {
        setQuery(opt.label);
        setIsOpen(false);
        onSelect({ product_name: opt.label, rate: opt.price });
    };

    return (
        <div ref={wrapperRef} className="relative">
            <input
                type="text" value={query} onChange={handleInputChange}
                onFocus={() => { if (options.length > 0) setIsOpen(true); }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        setIsOpen(false);
                    }
                }}
                placeholder={placeholder || "Search product..."}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {isLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                </div>
            )}
            {isOpen && options.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {options.map((opt) => (
                        <button key={opt.id} type="button" onClick={() => handleSelect(opt)}
                            className="w-full text-left px-3 py-2.5 hover:bg-purple-50 transition-colors border-b border-gray-50 last:border-0">
                            <div className="text-sm font-medium text-gray-800 truncate">{opt.label}</div>
                            <div className="text-xs text-gray-500">₹{opt.price}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════
// Spinner Component
// ═══════════════════════════════════════
const Spinner = ({ className = "h-4 w-4 border-white" }) => (
    <div className={`animate-spin rounded-full border-b-2 ${className}`}></div>
);

// ═══════════════════════════════════════
// Constants
// ═══════════════════════════════════════
const EMPTY_LABEL_FORM = {
    label_type: 'prepaid',
    customer_name: '',
    customer_address: '',
    pincode: '',
    mobile_number: '',
    alternate_number: '',
    amount: '',
    order_id: '',
};

const EMPTY_ITEM = { product_name: '', quantity: 1, rate: '', gst_percent: 0, is_include: true };

const PROFORMA_PAGE_SIZE = 10;

const EMPTY_PROFORMA_FORM = {
    customer_name: '',
    business_name: '',
    customer_phone: '',
    customer_alternate_phone: '',
    customer_address: '',
    customer_gst: '',
    discount_amount: '',
    items: [{ ...EMPTY_ITEM }],
};

// ═══════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════
function LabelInvoiceGenerator({ proformaOnly = false }) {
    // For admin, Label Generator and Proforma Invoice are separate sidebar sections /
    // pages. `proformaOnly` renders just the Proforma Invoice section; otherwise the
    // page shows only the label generator (admin) or the full tabbed UI (other roles).
    const admin = isAdminRole();
    const showLabelUI = !proformaOnly;

    // Main section tab
    const [mainTab, setMainTab] = useState(isB2bSalesperson() ? 'proforma' : 'inPacking'); // 'inPacking' | 'labels' | 'proforma'

    // Proforma section: its own page for admin (proformaOnly); a tab for other roles.
    const showProformaSection = proformaOnly || (!admin && mainTab === 'proforma');

    // ── Shipping Labels state ──
    const [labelForm, setLabelForm] = useState({ ...EMPTY_LABEL_FORM });
    const [loadingLabel, setLoadingLabel] = useState(false);
    const [orderLookupId, setOrderLookupId] = useState('');
    const [orderLookupData, setOrderLookupData] = useState(null);
    const [loadingOrderLookup, setLoadingOrderLookup] = useState(false);

    // ── Proforma Invoice state ──
    const [showProformaForm, setShowProformaForm] = useState(false); // list is the default view
    const [proformaMode, setProformaMode] = useState('standalone'); // 'order' | 'standalone'
    const [proformaOrderId, setProformaOrderId] = useState('');
    const [loadingProformaOrder, setLoadingProformaOrder] = useState(false);
    const [loadingOrderPrefill, setLoadingOrderPrefill] = useState(false);

    // Standalone form
    const [proformaForm, setProformaForm] = useState({ ...EMPTY_PROFORMA_FORM });
    const [loadingStandalone, setLoadingStandalone] = useState(false);

    // Existing proforma list (paginated + filters)
    const [proformaList, setProformaList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [proformaSearch, setProformaSearch] = useState('');
    const [proformaCreatedBy, setProformaCreatedBy] = useState('');
    const [salespersonOptions, setSalespersonOptions] = useState([]);
    const [proformaPage, setProformaPage] = useState(1);
    const [proformaMeta, setProformaMeta] = useState({ count: 0, next: null, previous: null });

    // Single source of truth for loading the list: initial load + whenever the
    // search query or sales-person filter changes. Search is debounced and ignores
    // partial queries (1-2 chars); an empty query loads the full list immediately.
    useEffect(() => {
        const q = proformaSearch.trim();
        if (q.length > 0 && q.length < 3) return;
        const timer = setTimeout(() => {
            fetchProformaList(undefined, 1, { search: q, createdBy: proformaCreatedBy });
        }, q ? 400 : 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proformaSearch, proformaCreatedBy]);

    // "Created By" filter is scoped to B2B salespersons only
    useEffect(() => {
        let active = true;
        getB2BSalespersonList()
            .then((res) => {
                if (!active || res?.status !== 200) return;
                const list = res.data?.results || res.data || [];
                setSalespersonOptions(
                    list
                        .map((u) => ({ value: u.id, label: u.name || u.username || u.email || `User ${u.id}` }))
                        .filter((o) => o.value != null)
                );
            })
            .catch((err) => console.error('Error fetching B2B salespersons:', err));
        return () => { active = false; };
    }, []);

    // ═══════════════════════════════════════
    // SHIPPING LABELS HANDLERS
    // ═══════════════════════════════════════

    const handleLabelFormChange = (field, value) => {
        if (field === 'mobile_number' || field === 'alternate_number') {
            value = sanitizeMobile(value);
        }
        setLabelForm(prev => ({ ...prev, [field]: value }));
    };

    const handleOrderLookup = async () => {
        const trimmed = orderLookupId.trim();
        if (!trimmed) { toast.error('Please enter an Order ID'); return; }
        setLoadingOrderLookup(true);
        setOrderLookupData(null);
        try {
            const response = await getOrderDetails(trimmed);
            if (response.status === 200 && response.data) {
                setOrderLookupData(response.data);
            } else {
                toast.error('Order not found');
            }
        } catch (error) {
            console.error('Order lookup error:', error);
            toast.error('Failed to fetch order details');
        }
        setLoadingOrderLookup(false);
    };

    const handleUseOrderDetails = () => {
        if (!orderLookupData) return;
        setLabelForm(prev => ({
            ...prev,
            customer_name: orderLookupData.user || '',
            mobile_number: orderLookupData.phone_number || '',
            customer_address: orderLookupData.shipping_user?.shipping_address || '',
            order_id: orderLookupData.id || '',
            amount: prev.label_type === 'cod' ? (orderLookupData.total_price || '') : prev.amount,
        }));
        toast.success('Order details applied to form');
    };

    const handleLabelSubmit = async (e) => {
        e.preventDefault();
        if (!labelForm.customer_name.trim()) { toast.error("Customer name is required"); return; }
        if (!labelForm.customer_address.trim()) { toast.error("Customer address is required"); return; }
        if (!labelForm.pincode.trim()) { toast.error("Pincode is required"); return; }
        if (!labelForm.mobile_number.trim()) { toast.error("Mobile number is required"); return; }
        if (labelForm.label_type === 'cod' && (!labelForm.amount || Number(labelForm.amount) <= 0)) {
            toast.error("Amount is required for COD labels"); return;
        }

        setLoadingLabel(true);
        try {
            const payload = {
                label_type: labelForm.label_type,
                customer_name: labelForm.customer_name.trim(),
                customer_address: labelForm.customer_address.trim(),
                pincode: labelForm.pincode.trim(),
                mobile_number: labelForm.mobile_number.trim(),
            };
            if (labelForm.alternate_number?.trim()) payload.alternate_number = labelForm.alternate_number.trim();
            if (labelForm.label_type === 'cod') payload.amount = Number(labelForm.amount);
            if (labelForm.order_id?.trim()) payload.order_id = labelForm.order_id.trim();

            const response = await createCustomLabel(payload);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            toast.success(`${labelForm.label_type === 'prepaid' ? 'Prepaid' : 'COD'} label generated!`);
        } catch (error) {
            console.error('Error generating label:', error);
            toast.error(error?.response?.data?.message || "Failed to generate label");
        }
        setLoadingLabel(false);
    };

    // ═══════════════════════════════════════
    // PROFORMA INVOICE HANDLERS
    // ═══════════════════════════════════════

    const fetchProformaList = async (orderId, page = 1, overrides = {}) => {
        const search = overrides.search !== undefined ? overrides.search : proformaSearch;
        const createdBy = overrides.createdBy !== undefined ? overrides.createdBy : proformaCreatedBy;
        setLoadingList(true);
        try {
            const response = await getProformaInvoices(orderId || undefined, {
                page,
                pageSize: PROFORMA_PAGE_SIZE,
                search: search?.trim() || undefined,
                createdBy: createdBy || undefined,
            });
            if (response.status === 200) {
                const data = response.data;
                const isArray = Array.isArray(data);
                const list = isArray ? data : (data?.results || []);
                setProformaList(list);
                setProformaMeta({
                    count: isArray ? list.length : (data?.count ?? list.length),
                    next: isArray ? null : (data?.next || null),
                    previous: isArray ? null : (data?.previous || null),
                });
                setProformaPage(page);
            }
        } catch (error) {
            console.error('Error fetching proforma list:', error);
        }
        setLoadingList(false);
    };

    // Pull an existing order's customer + items into the form so the preview fills in
    // and the details can be edited before generating.
    const handlePrefillFromOrder = async () => {
        const trimmed = proformaOrderId.trim();
        if (!trimmed) { toast.error("Please enter an Order ID"); return; }
        setLoadingOrderPrefill(true);
        try {
            const response = await getOrderDetails(trimmed);
            const order = response?.data;
            if (response?.status !== 200 || !order) { toast.error('Order not found'); return; }

            const orderItems = Array.isArray(order.order_items) ? order.order_items : [];
            const items = orderItems.map((oi) => {
                const quantity = Number(oi.quantity) || 1;
                const rate = Number(oi.price ?? oi.unit_price ?? 0)
                    || (Number(oi.total_price ?? 0) / quantity)
                    || 0;
                return {
                    ...EMPTY_ITEM,
                    product_name: oi.shop_product_aka_name || oi.aka_name || oi.product_name || oi.name || '',
                    quantity,
                    rate: Number.isFinite(rate) ? Number(rate.toFixed(2)) : 0,
                    gst_percent: Number(oi.gst_percent) || 0,
                };
            });

            setProformaForm({
                customer_name: order.user || order.name || '',
                business_name: order.shop_name || order.business_name || '',
                customer_phone: sanitizeMobile(order.phone_number || order.shipping_user?.phone_number || ''),
                customer_alternate_phone: sanitizeMobile(order.alternate_phone || order.shipping_user?.alternate_phone || ''),
                customer_address: order.shipping_user?.shipping_address || '',
                customer_gst: order.customer_gstin || '',
                discount_amount: Number(order.discount || order.discount_amount || 0) || '',
                items: items.length ? items : [{ ...EMPTY_ITEM }],
            });
            toast.success('Order details loaded into the preview');
        } catch (error) {
            console.error('Error prefilling from order:', error);
            toast.error('Failed to fetch order details');
        }
        setLoadingOrderPrefill(false);
    };

    const handleProformaFromOrder = async (e) => {
        e.preventDefault();
        if (!proformaOrderId.trim()) { toast.error("Please enter an Order ID"); return; }
        setLoadingProformaOrder(true);
        try {
            const response = await createProformaInvoice({ order_id: proformaOrderId.trim() });
            if (response.status === 200 || response.status === 201) {
                toast.success(response.data?.message || "Proforma invoice created!");
                if (response.data?.pdf_url) {
                    const pdfUrl = response.data.pdf_url.startsWith('http') ? response.data.pdf_url : `${BASE_URL}${response.data.pdf_url}`;
                    window.open(pdfUrl, '_blank');
                }
                setProformaOrderId('');
                setShowProformaForm(false);
                fetchProformaList(undefined, 1);
            }
        } catch (error) {
            console.error('Error creating proforma from order:', error);
            toast.error(error?.response?.data?.message || "Failed to create proforma invoice");
        }
        setLoadingProformaOrder(false);
    };

    // Standalone form handlers
    const handleProformaChange = (field, value) => {
        if (field === 'customer_phone' || field === 'customer_alternate_phone') {
            value = sanitizeMobile(value);
        }
        setProformaForm(prev => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setProformaForm(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, items };
        });
    };

    const handleProductSelect = (index, selected) => {
        setProformaForm(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], product_name: selected.product_name, rate: selected.rate };
            return { ...prev, items };
        });
    };

    const addItem = () => {
        const currentIsInclude = proformaForm.items[0]?.is_include !== false;
        setProformaForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM, is_include: currentIsInclude }] }));
    };

    const removeItem = (index) => {
        if (proformaForm.items.length <= 1) { toast.error("At least one item is required"); return; }
        setProformaForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
    };

    const calcItemTaxable = (item) => {
        const base = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
        if (item.is_include === false) {
            return base;
        } else {
            const gst = Number(item.gst_percent) || 0;
            return base - (base * gst / (100 + gst));
        }
    };
    const calcItemGst = (item) => {
        const base = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
        const gst = Number(item.gst_percent) || 0;
        if (!gst) return 0;
        if (item.is_include === false) {
            return base * gst / 100;
        } else {
            return base * gst / (100 + gst);
        }
    };
    const calcItemTotal = (item) => {
        const base = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
        if (item.is_include === false) {
            const gst = Number(item.gst_percent) || 0;
            return base + (base * gst / 100);
        } else {
            return base;
        }
    };
    const calcSubTotal = () => proformaForm.items.reduce((s, i) => s + calcItemTotal(i), 0);
    const calcGrandTotal = () => {
        const sub = calcSubTotal();
        const disc = Number(proformaForm.discount_amount) || 0;
        return Math.max(0, sub - disc);
    };

    // ── Live preview data (mirrors what the backend renders into the PDF) ──
    const previewRows = proformaForm.items
        .filter((i) => i.product_name?.trim() || Number(i.rate) > 0)
        .map((item) => ({
            display_name: item.product_name?.trim(),
            hsn_code: item.hsn_code || '',
            quantity: Number(item.quantity) || 0,
            rate: Number(item.rate) || 0,
            gst_percent: Number(item.gst_percent) || 0,
            gst_amount: calcItemGst(item),
            amount: calcItemTotal(item),
        }));
    const previewTotalQty = previewRows.reduce((s, r) => s + r.quantity, 0);
    const previewTotalGst = previewRows.reduce((s, r) => s + r.gst_amount, 0);

    const handleStandaloneSubmit = async (e) => {
        e.preventDefault();
        if (!proformaForm.customer_name.trim()) { toast.error("Customer name is required"); return; }
        const valid = proformaForm.items.some(i => i.product_name.trim() && Number(i.rate) > 0);
        if (!valid) { toast.error("At least one item with name and rate is required"); return; }

        setLoadingStandalone(true);
        try {
            const payload = { customer_name: proformaForm.customer_name.trim() };
            if (proformaForm.business_name.trim()) payload.business_name = proformaForm.business_name.trim();
            if (proformaForm.customer_phone.trim()) payload.customer_phone = proformaForm.customer_phone.trim();
            if (proformaForm.customer_alternate_phone.trim()) payload.customer_alternate_phone = proformaForm.customer_alternate_phone.trim();
            if (proformaForm.customer_address.trim()) payload.customer_address = proformaForm.customer_address.trim();
            if (proformaForm.customer_gst.trim()) payload.customer_gst = proformaForm.customer_gst.trim();
            if (Number(proformaForm.discount_amount) > 0) payload.discount_amount = Number(proformaForm.discount_amount);
            payload.items = proformaForm.items.filter(i => i.product_name.trim()).map(item => {
                const m = {
                    product_name: item.product_name.trim(),
                    quantity: Number(item.quantity) || 1,
                    rate: Number(item.rate) || 0,
                    is_include: item.is_include !== false
                };
                if (Number(item.gst_percent)) m.gst_percent = Number(item.gst_percent);
                return m;
            });

            const response = await createProformaInvoice(payload);
            if (response.status === 200 || response.status === 201) {
                toast.success(response.data?.message || "Proforma invoice created!");
                if (response.data?.pdf_url) {
                    const pdfUrl = response.data.pdf_url.startsWith('http') ? response.data.pdf_url : `${BASE_URL}${response.data.pdf_url}`;
                    window.open(pdfUrl, '_blank');
                }
                setProformaForm({ ...EMPTY_PROFORMA_FORM, items: [{ ...EMPTY_ITEM }] });
                setShowProformaForm(false);
                fetchProformaList(undefined, 1);
            }
        } catch (error) {
            console.error('Error creating standalone proforma:', error);
            toast.error(error?.response?.data?.message || "Failed to create proforma invoice");
        }
        setLoadingStandalone(false);
    };

    const handleDeleteProforma = async (proformaId) => {
        if (!window.confirm("Delete this proforma invoice?")) return;
        try {
            const response = await deleteProformaInvoice(proformaId);
            if (response.status === 200 || response.status === 204) {
                toast.success("Proforma invoice deleted");
                // Stay on the current page unless it just became empty.
                const nextPage = proformaList.length === 1 && proformaPage > 1 ? proformaPage - 1 : proformaPage;
                fetchProformaList(undefined, nextPage);
            }
        } catch (error) {
            console.error('Error deleting proforma:', error);
            toast.error("Failed to delete proforma invoice");
        }
    };

    // Filter changes just update state — the list effect refetches (debounced).
    const handleClearProformaFilters = () => {
        setProformaSearch('');
        setProformaCreatedBy('');
    };

    const goToProformaPage = (page) => {
        if (page < 1 || loadingList) return;
        fetchProformaList(undefined, page);
    };

    const proformaTotalPages = Math.max(1, Math.ceil((proformaMeta.count || 0) / PROFORMA_PAGE_SIZE));

    // ═══════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════

    return (
        <div className="w-full flex-1">
            <StyledHeadingText
                text={proformaOnly ? "Proforma Invoice" : admin ? "Label Generator" : "Label & Invoice Generator"}
                TextColor="text-gray-700 font-semibold"
            />

            {/* ── Main Section Tabs ── */}
            {showLabelUI && !isB2bSalesperson() && (
                <div className="mt-4 flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                    <button onClick={() => setMainTab('inPacking')}
                        className={`px-5 py-2.5 text-sm font-medium rounded-md transition-all ${mainTab === 'inPacking' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                        B2C Labels
                    </button>
                    <button onClick={() => setMainTab('b2bLabels')}
                        className={`px-5 py-2.5 text-sm font-medium rounded-md transition-all ${mainTab === 'b2bLabels' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                        🚚 B2B Labels
                    </button>
                    <button onClick={() => setMainTab('labels')}
                        className={`px-5 py-2.5 text-sm font-medium rounded-md transition-all ${mainTab === 'labels' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                        📦 Additional Shipping Labels
                    </button>
                    {!admin && (
                        <button onClick={() => setMainTab('proforma')}
                            className={`px-5 py-2.5 text-sm font-medium rounded-md transition-all ${mainTab === 'proforma' ? 'bg-white shadow text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}>
                            📄 Proforma Invoice
                        </button>
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════ */}
            {/* SECTION 1: SHIPPING LABELS         */}
            {/* ═══════════════════════════════════ */}
            {showLabelUI && mainTab === 'labels' && (
                <form onSubmit={handleLabelSubmit} className="mt-5 space-y-5">

                    {/* Order Lookup */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3">🔍 Lookup Order (Optional — auto-fill details)</p>
                        <div className="flex gap-3">
                            <input
                                type="text" placeholder="Enter Order ID to auto-fill..."
                                value={orderLookupId} onChange={(e) => setOrderLookupId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleOrderLookup())}
                                className="flex-1 max-w-md px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            />
                            <button type="button" onClick={handleOrderLookup} disabled={loadingOrderLookup}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
                                {loadingOrderLookup ? <Spinner /> : '🔍'} Lookup
                            </button>
                        </div>

                        {/* Order Details Card */}
                        {orderLookupData && (
                            <div className="mt-3 p-4 bg-white rounded-lg border border-blue-200 shadow-sm">
                                <div className="flex items-start justify-between">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm flex-1">
                                        <div className="flex flex-col">
                                            <span className="text-gray-400 text-xs">Customer</span>
                                            <span className="font-bold text-gray-800">{orderLookupData.user}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-gray-400 text-xs">Phone</span>
                                            <span className="font-bold text-gray-800">{orderLookupData.phone_number}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-gray-400 text-xs">Total</span>
                                            <span className="font-bold text-gray-800">₹{orderLookupData.total_price}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-gray-400 text-xs">Status</span>
                                            <span className={`font-bold text-xs ${orderLookupData.status === 'DELIVERED' ? 'text-green-600' :
                                                orderLookupData.status === 'CANCELLED' ? 'text-red-600' : 'text-yellow-600'
                                                }`}>{orderLookupData.status}</span>
                                        </div>
                                        <div className="flex flex-col col-span-2">
                                            <span className="text-gray-400 text-xs">Address</span>
                                            <span className="text-gray-700 text-xs">{orderLookupData.shipping_user?.shipping_address || '—'}</span>
                                        </div>
                                    </div>
                                    <button type="button" onClick={handleUseOrderDetails}
                                        className="ml-3 flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors">
                                        ↙ Use Details
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-lg border p-5">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Additional Shipping Labels</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Label Type */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-600">Label Type <span className="text-red-500">*</span></label>
                                <select value={labelForm.label_type} onChange={(e) => handleLabelFormChange('label_type', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                    <option value="prepaid">📦 Prepaid</option>
                                    <option value="cod">💰 COD</option>
                                </select>
                            </div>

                            {/* Customer Name */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-600">Customer Name <span className="text-red-500">*</span></label>
                                <input type="text" value={labelForm.customer_name} onChange={(e) => handleLabelFormChange('customer_name', e.target.value)}
                                    placeholder="e.g. Ramesh Kumar" required
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>

                            {/* Mobile */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-600">Mobile Number <span className="text-red-500">*</span></label>
                                <input type="text" value={labelForm.mobile_number} onChange={(e) => handleLabelFormChange('mobile_number', e.target.value)}
                                    placeholder="e.g. 9876543210" required
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>

                            {/* Alternate Number */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-600">Alternate Number <span className="text-gray-400 text-[10px]">(Optional)</span></label>
                                <input type="text" value={labelForm.alternate_number} onChange={(e) => handleLabelFormChange('alternate_number', e.target.value)}
                                    placeholder="Optional"
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>

                            {/* Pincode */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-600">Pincode <span className="text-red-500">*</span></label>
                                <input type="text" value={labelForm.pincode} onChange={(e) => handleLabelFormChange('pincode', e.target.value)}
                                    placeholder="e.g. 452001" maxLength={6} required
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>

                            {/* Amount (COD only) */}
                            {labelForm.label_type === 'cod' && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-600">Amount (₹) <span className="text-red-500">*</span></label>
                                    <input type="number" min="0" step="0.01" value={labelForm.amount} onChange={(e) => handleLabelFormChange('amount', e.target.value)}
                                        placeholder="e.g. 15000" required
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                            )}

                            {/* Order ID (optional) */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-600">Order ID <span className="text-gray-400 text-[10px]">(Optional)</span></label>
                                <input type="text" value={labelForm.order_id} onChange={(e) => handleLabelFormChange('order_id', e.target.value)}
                                    placeholder="UUID (optional)"
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                        </div>

                        {/* Address */}
                        <div className="mt-4 flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-600">Customer Address <span className="text-red-500">*</span></label>
                            <textarea value={labelForm.customer_address} onChange={(e) => handleLabelFormChange('customer_address', e.target.value)}
                                placeholder="Full shipping address (multi-line)" rows={3} required
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
                        </div>
                    </div>

                    {/* Submit */}
                    <button type="submit" disabled={loadingLabel}
                        className="px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                        {loadingLabel ? <Spinner /> : <span>{labelForm.label_type === 'cod' ? '💰' : '📦'}</span>}
                        Generate {labelForm.label_type === 'cod' ? 'COD' : 'Prepaid'} Label
                    </button>
                </form>
            )}

            {showLabelUI && mainTab === 'inPacking' && <InPackingOrders />}
            {showLabelUI && mainTab === 'b2bLabels' && <B2BPackagingLabels />}

            {/* ═══════════════════════════════════ */}
            {/* SECTION 2: PROFORMA INVOICE         */}
            {/* ═══════════════════════════════════ */}
            {showProformaSection && (
                <div className="mt-5 space-y-6">
                    {/* Sub-tabs — only while the create form is open */}
                    {showProformaForm && (
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                                <button onClick={() => setProformaMode('standalone')}
                                    className={`px-4 py-2 text-xs font-medium rounded-md transition-all ${proformaMode === 'standalone' ? 'bg-white shadow text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                    ✍️ Standalone
                                </button>
                                <button onClick={() => setProformaMode('order')}
                                    className={`px-4 py-2 text-xs font-medium rounded-md transition-all ${proformaMode === 'order' ? 'bg-white shadow text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                    📋 From Order
                                </button>
                            </div>
                            <button onClick={() => setShowProformaForm(false)}
                                className="ml-auto px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                                ✕ Close Form
                            </button>
                        </div>
                    )}

                    {/* ── From Order ── */}
                    {showProformaForm && proformaMode === 'order' && (
                        <form onSubmit={handleProformaFromOrder} className="bg-white rounded-lg shadow-lg border p-5">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Generate from Order</h3>
                            <div className="flex gap-3 flex-wrap">
                                <input type="text" placeholder="Enter Order ID (UUID)..." value={proformaOrderId}
                                    onChange={(e) => setProformaOrderId(e.target.value)}
                                    className="flex-1 min-w-[220px] max-w-lg px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                <button type="button" onClick={handlePrefillFromOrder} disabled={loadingOrderPrefill}
                                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                                    {loadingOrderPrefill ? <Spinner /> : <span>🔍</span>}
                                    Load &amp; Preview
                                </button>
                                <button type="submit" disabled={loadingProformaOrder}
                                    className="px-6 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                                    {loadingProformaOrder ? <Spinner /> : <span>📄</span>}
                                    Generate
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                "Load &amp; Preview" pulls the order's customer and items into the preview below. "Generate" creates the proforma from the order on the server.
                            </p>
                        </form>
                    )}

                    {/* ── Standalone Form ── */}
                    {showProformaForm && proformaMode === 'standalone' && (
                        <form onSubmit={handleStandaloneSubmit} className="space-y-5">
                            {/* Customer Details */}
                            <div className="bg-white rounded-lg shadow-lg border p-5">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Customer Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-600">Customer Name <span className="text-red-500">*</span></label>
                                        <input type="text" value={proformaForm.customer_name} onChange={(e) => handleProformaChange('customer_name', e.target.value)}
                                            placeholder="e.g. Ramesh Kumar" required
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-600">Business Name</label>
                                        <input type="text" value={proformaForm.business_name} onChange={(e) => handleProformaChange('business_name', e.target.value)}
                                            placeholder="e.g. Kumar Agro Services"
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-600">Phone</label>
                                        <input type="text" value={proformaForm.customer_phone} onChange={(e) => handleProformaChange('customer_phone', e.target.value)}
                                            placeholder="e.g. 9876543210"
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-600">Alternate Phone</label>
                                        <input type="text" value={proformaForm.customer_alternate_phone} onChange={(e) => handleProformaChange('customer_alternate_phone', e.target.value)}
                                            placeholder="Optional"
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-600">GST Number</label>
                                        <input type="text" value={proformaForm.customer_gst} onChange={(e) => handleProformaChange('customer_gst', e.target.value)}
                                            placeholder="e.g. 23AABCU9603R1ZM"
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-600">Address</label>
                                        <textarea value={proformaForm.customer_address} onChange={(e) => handleProformaChange('customer_address', e.target.value)}
                                            placeholder="Full address" rows={2}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none" />
                                    </div>
                                </div>
                            </div>

                            {/* Items */}
                            <div className="bg-white rounded-lg shadow-lg border p-5">
                                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Items</h3>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                            <span>Tax Mode:</span>
                                            <select
                                                value={proformaForm.items[0]?.is_include !== false}
                                                onChange={(e) => {
                                                    const isInclude = e.target.value === 'true';
                                                    setProformaForm(prev => ({
                                                        ...prev,
                                                        items: prev.items.map(item => ({ ...item, is_include: isInclude }))
                                                    }));
                                                }}
                                                className="px-2 py-1 border border-gray-300 rounded bg-white font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                            >
                                                <option value="true">GST Inclusive</option>
                                                <option value="false">GST Exclusive</option>
                                            </select>
                                        </div>
                                        <button type="button" onClick={addItem}
                                            className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1">
                                            + Add Item
                                        </button>
                                    </div>
                                </div>

                                {/* Items Table Header (desktop) */}
                                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 rounded-t-lg text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <div className="col-span-4">Product</div>
                                    <div className="col-span-1">Qty</div>
                                    <div className="col-span-2">Rate ({proformaForm.items[0]?.is_include !== false ? 'Incl. GST' : 'Excl. GST'})</div>
                                    <div className="col-span-1">GST%</div>
                                    <div className="col-span-1">Taxable Amt</div>
                                    <div className="col-span-1">GST Amt</div>
                                    <div className="col-span-1">Amount</div>
                                    <div className="col-span-1 text-center">✕</div>
                                </div>

                                <div className="space-y-0 divide-y divide-gray-100">
                                    {proformaForm.items.map((item, index) => (
                                        <div key={index}>
                                            {/* Desktop row */}
                                            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50 transition-colors">
                                                <div className="col-span-4">
                                                    <ProductSearchInput value={item.product_name} placeholder="Search product..."
                                                        onChange={(text) => handleItemChange(index, 'product_name', text)}
                                                        onSelect={(s) => handleProductSelect(index, s)} />
                                                </div>
                                                <div className="col-span-1">
                                                    <input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center" />
                                                </div>
                                                <div className="col-span-2">
                                                    <input type="number" min="0" step="0.01" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                                        placeholder="0.00" className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                                </div>
                                                <div className="col-span-1">
                                                    <input type="number" min="0" max="100" step="0.01" value={item.gst_percent} onChange={(e) => handleItemChange(index, 'gst_percent', e.target.value)}
                                                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center" />
                                                </div>
                                                <div className="col-span-1">
                                                    <span className="text-sm text-gray-700 font-medium">₹{calcItemTaxable(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                                <div className="col-span-1">
                                                    <span className="text-sm text-gray-500">₹{calcItemGst(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                                <div className="col-span-1">
                                                    <span className="text-sm font-bold text-green-700">₹{calcItemTotal(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                                <div className="col-span-1 text-center">
                                                    {proformaForm.items.length > 1 && (
                                                        <button type="button" onClick={() => removeItem(index)}
                                                            className="text-red-500 hover:text-red-700 font-bold text-lg transition-colors">✕</button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Mobile card */}
                                            <div className="md:hidden p-4 bg-gray-50 rounded-lg border border-gray-200 my-2">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-xs font-semibold text-gray-500">Item {index + 1}</span>
                                                    {proformaForm.items.length > 1 && (
                                                        <button type="button" onClick={() => removeItem(index)}
                                                            className="text-red-500 hover:text-red-700 text-xs font-medium">✕ Remove</button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="col-span-2 flex flex-col gap-1">
                                                        <label className="text-xs text-gray-500">Product <span className="text-red-500">*</span></label>
                                                        <ProductSearchInput
                                                            value={item.product_name}
                                                            placeholder="Search or type product name..."
                                                            onChange={(text) => handleItemChange(index, 'product_name', text)}
                                                            onSelect={(s) => handleProductSelect(index, s)}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-xs text-gray-500">Qty</label>
                                                        <input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-xs text-gray-500">Rate ({proformaForm.items[0]?.is_include !== false ? 'Incl. GST' : 'Excl. GST'})</label>
                                                        <input type="number" min="0" step="0.01" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} placeholder="0"
                                                            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-xs text-gray-500">GST %</label>
                                                        <input type="number" min="0" max="100" value={item.gst_percent} onChange={(e) => handleItemChange(index, 'gst_percent', e.target.value)}
                                                            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                                    </div>
                                                    <div className="col-span-2 flex justify-between text-sm pt-2 border-t">
                                                        <span>Taxable: ₹{calcItemTaxable(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        <span>GST: ₹{calcItemGst(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        <span className="font-bold text-green-700">Amt: ₹{calcItemTotal(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Discount input + summary */}
                                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                                    <div className="flex items-center justify-between gap-4">
                                        <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Discount (₹)</label>
                                        <input
                                            type="number" min="0" step="0.01"
                                            value={proformaForm.discount_amount}
                                            onChange={(e) => handleProformaChange('discount_amount', e.target.value)}
                                            placeholder="0.00"
                                            className="w-36 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500">Sub Total</span>
                                        <span className="text-sm text-gray-700">₹{calcSubTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                    </div>

                                    {Number(proformaForm.discount_amount) > 0 && (
                                        <div className="flex items-center justify-between text-red-600">
                                            <span className="text-sm font-medium">Discount</span>
                                            <span className="text-sm font-medium">− ₹{Number(proformaForm.discount_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-2 border-t border-gray-300">
                                        <span className="text-sm font-semibold text-gray-700">Grand Total</span>
                                        <span className="text-lg font-bold text-gray-900">₹{calcGrandTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            </div>

                            <button type="submit" disabled={loadingStandalone}
                                className="px-8 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                                {loadingStandalone ? <Spinner /> : <span>📄</span>}
                                Generate Proforma Invoice
                            </button>
                        </form>
                    )}

                    {/* ── Live PDF Preview (replaces the list while the form is open) ── */}
                    {showProformaForm && (
                        <div className="bg-white rounded-lg shadow-lg border p-5">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                                Preview
                                <span className="ml-2 normal-case font-normal text-gray-400">— how the generated PDF will look</span>
                            </h3>
                            <div className="overflow-x-auto bg-gray-100 rounded-lg p-2 sm:p-4">
                                <div className="min-w-[750px] bg-white shadow-sm">
                                    <ProformaInvoicePreview
                                        customer={proformaForm}
                                        rows={previewRows}
                                        discountAmount={Number(proformaForm.discount_amount) || 0}
                                        subTotal={calcSubTotal()}
                                        grandTotal={calcGrandTotal()}
                                        totalQty={previewTotalQty}
                                        totalGstAmount={previewTotalGst}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Existing Proforma Invoices List ── */}
                    {!showProformaForm && (
                    <div className="bg-white rounded-lg shadow-lg border p-5">
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            <input type="text" placeholder="Search name / number (min 3 chars)..." value={proformaSearch}
                                onChange={(e) => setProformaSearch(e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-56" />
                            <select value={proformaCreatedBy} onChange={(e) => setProformaCreatedBy(e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-52">
                                <option value="">Filter by Sales Person</option>
                                {salespersonOptions.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            <button onClick={handleClearProformaFilters}
                                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors">
                                Clear
                            </button>
                            {loadingList && <Spinner className="h-4 w-4 border-gray-400" />}
                            <button
                                onClick={() => { setProformaMode('standalone'); setShowProformaForm(true); }}
                                className="ml-auto px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors">
                                + Add Proforma Invoice
                            </button>
                        </div>

                        {proformaList.length > 0 ? (
                            <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600">
                                            <th className="text-left px-4 py-2.5 rounded-l-lg font-medium">Proforma No</th>
                                            <th className="text-left px-4 py-2.5 font-medium">Customer</th>
                                            <th className="text-left px-4 py-2.5 font-medium">Business</th>
                                            <th className="text-left px-4 py-2.5 font-medium">Created By</th>
                                            <th className="text-left px-4 py-2.5 font-medium">Converted to Order</th>
                                            <th className="text-right px-4 py-2.5 rounded-r-lg font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {proformaList.map((pi) => {
                                            const isConverted = Boolean(pi.is_converted_to_order);
                                            const convertedOrderId = pi.converted_order_id || pi.order_id || null;
                                            return (
                                            <tr key={pi.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 font-medium text-gray-800">{pi.proforma_number || '-'}</td>
                                                <td className="px-4 py-3 text-gray-600">
                                                    <div className="font-medium text-gray-800">{pi.customer_name || '-'}</div>
                                                    {(pi.customer_phone || pi.customer_alternate_phone) && (
                                                        <div className="text-xs text-gray-400">{pi.customer_phone || pi.customer_alternate_phone}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-gray-600">{pi.business_name || '-'}</td>
                                                <td className="px-4 py-3 text-gray-600">
                                                    {pi.created_by_name || (pi.created_by ? '—' : <span className="text-gray-400 italic">System</span>)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isConverted ? (
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">Yes</span>
                                                            {convertedOrderId && (
                                                                <Link to={`/orders/${convertedOrderId}`}
                                                                    className="text-xs font-medium text-blue-600 hover:underline">
                                                                    View Order
                                                                </Link>
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">No</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                                        {isConverted ? (
                                                            <span
                                                                title="Already converted to an order; cannot edit."
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-400 text-xs font-medium rounded-md cursor-not-allowed"
                                                            >
                                                                Edit
                                                            </span>
                                                        ) : (
                                                            <Link
                                                                to={`/label-invoice/proforma/${pi.id}`}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-md hover:bg-purple-100 transition-colors"
                                                            >
                                                                Edit
                                                            </Link>
                                                        )}
                                                        {pi.pdf_url && (
                                                            <a href={pi.pdf_url.startsWith('http') ? pi.pdf_url : `${BASE_URL}${pi.pdf_url}`}
                                                                target="_blank" rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-100 transition-colors">
                                                                View PDF
                                                            </a>
                                                        )}
                                                        {!isConverted && (
                                                            <ConvertProformaToOrderButton
                                                                proforma={{ id: pi.id, isConverted }}
                                                                onSuccess={() => fetchProformaList(undefined, proformaPage)}
                                                                className="!px-2.5 !py-1 !text-xs !rounded-md !shadow-none"
                                                            />
                                                        )}
                                                        <button
                                                            onClick={() => !isConverted && handleDeleteProforma(pi.id)}
                                                            disabled={isConverted}
                                                            title={isConverted ? 'Already converted to an order; cannot delete.' : undefined}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-md hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-50">
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
                                <span className="text-xs text-gray-500">
                                    {proformaMeta.count} total · page {proformaPage} of {proformaTotalPages}
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => goToProformaPage(proformaPage - 1)}
                                        disabled={loadingList || (!proformaMeta.previous && proformaPage <= 1)}
                                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                        ← Prev
                                    </button>
                                    <button
                                        onClick={() => goToProformaPage(proformaPage + 1)}
                                        disabled={loadingList || (!proformaMeta.next && proformaPage >= proformaTotalPages)}
                                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                        Next →
                                    </button>
                                </div>
                            </div>
                            </>
                        ) : (
                            <div className="py-8 text-center">
                                <span className="text-sm text-gray-400 italic">
                                    {loadingList ? 'Loading...' : 'No proforma invoices found'}
                                </span>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default LabelInvoiceGenerator;
